import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/lib/api';
import { toast } from 'sonner';
import ImageUpload from '@/components/ImageUpload';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Loader2, 
  Gift,
  Sparkles,
  Eye,
  EyeOff,
  Calendar,
  Percent,
  Image as ImageIcon,
  Upload,
  Link as LinkIcon,
  Tag,
  ExternalLink,
  Lock,
  Crown
} from 'lucide-react';

const Promotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    image_url: '',
    discount_text: '',
    start_date: '',
    end_date: '',
    active: true,
    show_popup: true,
    linked_dish_ids: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [promoRes, restaurantRes] = await Promise.all([
        api.get('/promotions'),
        api.get('/restaurant')
      ]);
      setPromotions(promoRes.data);
      setRestaurant(restaurantRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (promotion = null) => {
    if (promotion) {
      setEditingPromotion(promotion);
      setForm({
        title: promotion.title || '',
        description: promotion.description || '',
        image_url: promotion.image_url || '',
        discount_text: promotion.discount_text || '',
        start_date: promotion.start_date?.split('T')[0] || '',
        end_date: promotion.end_date?.split('T')[0] || '',
        active: promotion.active !== false,
        show_popup: promotion.show_popup !== false,
        linked_dish_ids: promotion.linked_dish_ids || []
      });
    } else {
      setEditingPromotion(null);
      setForm({
        title: '',
        description: '',
        image_url: '',
        discount_text: '',
        start_date: '',
        end_date: '',
        active: true,
        show_popup: true,
        linked_dish_ids: []
      });
    }
    setDialogOpen(true);
  };

  const savePromotion = async () => {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error('Le titre et la description sont requis');
      return;
    }
    
    setSaving(true);
    try {
      const data = {
        ...form,
        start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null
      };
      
      if (editingPromotion) {
        await api.put(`/promotions/${editingPromotion.id}`, data);
        toast.success('Promotion mise à jour');
      } else {
        await api.post('/promotions', data);
        toast.success('Promotion créée');
      }
      
      fetchData();
      setDialogOpen(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const togglePromotion = async (promotion) => {
    try {
      await api.put(`/promotions/${promotion.id}/toggle`);
      toast.success(promotion.active ? 'Promotion désactivée' : 'Promotion activée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const deletePromotion = async (id) => {
    if (!window.confirm('Supprimer cette promotion ?')) return;
    try {
      await api.delete(`/promotions/${id}`);
      toast.success('Promotion supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const generateWithAI = async () => {
    if (!form.title.trim()) {
      toast.error('Entrez d\'abord un titre pour la promotion');
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.post('/ai/suggest-promotion');
      // Parse AI response to fill description
      setForm(prev => ({ ...prev, description: response.data.promotion }));
      toast.success('Description générée par l\'IA');
    } catch (error) {
      toast.error('Erreur lors de la génération');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-64" />)}
        </div>
      </div>
    );
  }

  const activePromotions = promotions.filter(p => p.active);
  const inactivePromotions = promotions.filter(p => !p.active);

  // Check if user has access (Essentiel+ only)
  const hasAccess = restaurant?.subscription_plan !== 'starter';

  if (!hasAccess) {
    return (
      <div className="space-y-6" data-testid="promotions-page">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Gift className="h-8 w-8 text-pink-500" />
            Promotions
            <Badge className="bg-amber-100 text-amber-700">Essentiel+</Badge>
          </h1>
          <p className="text-gray-500 mt-1">
            Créez des offres spéciales qui s'affichent en popup sur votre menu
          </p>
        </div>

        <Card className="border-0 shadow-soft">
          <CardContent className="p-8 text-center">
            <Lock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold text-gray-900 mb-2">
              Fonctionnalité Essentiel / Premium
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Créez des promotions attractives qui s'affichent automatiquement 
              sur votre menu client. Suivez leurs performances en temps réel.
            </p>
            <ul className="text-sm text-gray-500 mb-6 space-y-2 max-w-xs mx-auto text-left">
              <li className="flex items-center gap-2">
                <Gift className="h-4 w-4 text-pink-500" />
                Popup promotionnel sur le menu
              </li>
              <li className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-pink-500" />
                Création assistée par IA
              </li>
              <li className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-pink-500" />
                Suivi des vues et clics
              </li>
            </ul>
            <Button
              onClick={() => window.location.href = '/billing'}
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
            >
              <Crown className="h-4 w-4 mr-2" />
              Passer à Essentiel
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="promotions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Gift className="h-8 w-8 text-pink-500" />
            Promotions
          </h1>
          <p className="text-gray-500 mt-1">
            Créez des offres spéciales qui s'affichent en popup sur votre menu
          </p>
        </div>
        <Button 
          onClick={() => openDialog()}
          className="bg-pink-500 hover:bg-pink-600 text-white rounded-full"
          data-testid="add-promotion-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nouvelle promotion
        </Button>
      </div>

      {/* Preview Link */}
      {restaurant && (
        <Card className="border-0 shadow-soft bg-gradient-to-r from-pink-50 to-white">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-pink-100 text-pink-500">
                <Eye className="h-5 w-5" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Prévisualiser le popup</p>
                <p className="text-sm text-gray-500">Testez l'affichage de vos promotions sur le menu client</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => window.open(`/m/${restaurant.slug}`, '_blank')}
              className="rounded-full"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Voir le menu
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Active Promotions */}
      {activePromotions.length > 0 && (
        <div>
          <h2 className="font-heading font-semibold text-lg text-gray-900 mb-4 flex items-center gap-2">
            <Badge className="bg-emerald-100 text-emerald-700 px-2 py-0.5">
              {activePromotions.length} active{activePromotions.length > 1 ? 's' : ''}
            </Badge>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {activePromotions.map((promotion) => (
              <PromotionCard 
                key={promotion.id}
                promotion={promotion}
                onEdit={() => openDialog(promotion)}
                onToggle={() => togglePromotion(promotion)}
                onDelete={() => deletePromotion(promotion.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Inactive Promotions */}
      {inactivePromotions.length > 0 && (
        <div>
          <h2 className="font-heading font-semibold text-lg text-gray-500 mb-4">
            Promotions inactives ({inactivePromotions.length})
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {inactivePromotions.map((promotion) => (
              <PromotionCard 
                key={promotion.id}
                promotion={promotion}
                onEdit={() => openDialog(promotion)}
                onToggle={() => togglePromotion(promotion)}
                onDelete={() => deletePromotion(promotion.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {promotions.length === 0 && (
        <Card className="border-0 shadow-soft">
          <CardContent className="py-16 text-center">
            <div className="h-16 w-16 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-4">
              <Gift className="h-8 w-8 text-pink-500" />
            </div>
            <h3 className="font-heading font-semibold text-xl text-gray-900 mb-2">
              Aucune promotion
            </h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Créez votre première promotion pour attirer l'attention de vos clients 
              avec un popup attractif sur votre menu digital.
            </p>
            <Button 
              onClick={() => openDialog()}
              className="bg-pink-500 hover:bg-pink-600 text-white rounded-full"
            >
              <Plus className="mr-2 h-4 w-4" />
              Créer ma première promotion
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingPromotion ? 'Modifier la promotion' : 'Nouvelle promotion'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Titre de la promotion *</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Ex: Happy Hour, Menu du jour, -20% sur les desserts..."
                data-testid="promo-title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="discount_text">Badge promotionnel</Label>
              <div className="flex gap-2">
                <Input
                  id="discount_text"
                  value={form.discount_text}
                  onChange={(e) => setForm(prev => ({ ...prev, discount_text: e.target.value }))}
                  placeholder="Ex: -20%, 2+1 gratuit, Menu à 15€..."
                  className="flex-1"
                  data-testid="promo-discount"
                />
                <div className="flex gap-1">
                  {['-10%', '-20%', '2+1'].map(tag => (
                    <Button
                      key={tag}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setForm(prev => ({ ...prev, discount_text: tag }))}
                      className="text-xs"
                    >
                      {tag}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="description">Description *</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={generateWithAI}
                  disabled={saving}
                  className="h-8 text-xs text-pink-500 hover:text-pink-600"
                >
                  <Sparkles className="h-3 w-3 mr-1" />
                  Générer avec l'IA
                </Button>
              </div>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Décrivez votre offre de manière attractive..."
                rows={3}
                data-testid="promo-description"
              />
            </div>

            <div className="space-y-2">
              <Label>Image de la promotion</Label>
              <Tabs defaultValue="upload" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="upload" className="text-sm">
                    <Upload className="h-4 w-4 mr-2" />
                    Importer
                  </TabsTrigger>
                  <TabsTrigger value="url" className="text-sm">
                    <LinkIcon className="h-4 w-4 mr-2" />
                    URL
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="upload" className="mt-3">
                  <ImageUpload
                    value={form.image_url}
                    onChange={(url) => setForm(prev => ({ ...prev, image_url: url }))}
                    aspectRatio="landscape"
                    placeholder="Importer une image"
                  />
                </TabsContent>
                <TabsContent value="url" className="mt-3">
                  <Input
                    value={form.image_url}
                    onChange={(e) => setForm(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="https://..."
                  />
                </TabsContent>
              </Tabs>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">Date de début</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">Date de fin</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900 text-sm">Promotion active</p>
                  <p className="text-xs text-gray-500">Activer ou désactiver cette promotion</p>
                </div>
                <Switch
                  checked={form.active}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, active: checked }))}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-pink-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900 text-sm">Afficher en popup</p>
                  <p className="text-xs text-gray-500">Afficher un popup sur le menu client</p>
                </div>
                <Switch
                  checked={form.show_popup}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, show_popup: checked }))}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Annuler</Button>
            </DialogClose>
            <Button 
              onClick={savePromotion}
              disabled={saving}
              className="bg-pink-500 hover:bg-pink-600 text-white"
              data-testid="save-promo-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingPromotion ? 'Mettre à jour' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Promotion Card Component
const PromotionCard = ({ promotion, onEdit, onToggle, onDelete }) => {
  const isExpired = promotion.end_date && new Date(promotion.end_date) < new Date();
  
  return (
    <Card 
      className={`border-0 shadow-soft overflow-hidden transition-all ${
        !promotion.active ? 'opacity-60' : ''
      }`}
      data-testid={`promo-card-${promotion.id}`}
    >
      {promotion.image_url ? (
        <div className="relative h-36">
          <img 
            src={promotion.image_url} 
            alt={promotion.title}
            className="w-full h-full object-cover"
          />
          {promotion.discount_text && (
            <Badge className="absolute top-3 right-3 bg-pink-500 text-white font-bold">
              {promotion.discount_text}
            </Badge>
          )}
        </div>
      ) : (
        <div className="h-24 bg-gradient-to-br from-pink-100 to-pink-50 flex items-center justify-center relative">
          <Gift className="h-10 w-10 text-pink-300" />
          {promotion.discount_text && (
            <Badge className="absolute top-3 right-3 bg-pink-500 text-white font-bold">
              {promotion.discount_text}
            </Badge>
          )}
        </div>
      )}
      
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-heading font-semibold text-gray-900 line-clamp-1">
            {promotion.title}
          </h3>
          <div className="flex gap-1 flex-shrink-0">
            {promotion.show_popup && (
              <Badge variant="outline" className="text-xs bg-pink-50 border-pink-200 text-pink-600">
                Popup
              </Badge>
            )}
          </div>
        </div>
        
        <p className="text-sm text-gray-500 line-clamp-2 mb-3">
          {promotion.description}
        </p>

        {(promotion.start_date || promotion.end_date) && (
          <div className="flex items-center gap-1 text-xs text-gray-400 mb-3">
            <Calendar className="h-3 w-3" />
            {promotion.start_date && new Date(promotion.start_date).toLocaleDateString('fr-FR')}
            {promotion.start_date && promotion.end_date && ' - '}
            {promotion.end_date && new Date(promotion.end_date).toLocaleDateString('fr-FR')}
            {isExpired && <Badge variant="destructive" className="ml-2 text-xs">Expirée</Badge>}
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <Switch 
            checked={promotion.active}
            onCheckedChange={onToggle}
          />
          <div className="flex gap-1">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onEdit}
              className="h-8 w-8 p-0"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onDelete}
              className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default Promotions;
